import { LocationDescriptorObject } from 'history';
import * as qs from 'qs';
import { createSelector } from 'reselect';
import { ApplicationState } from '../store';

export const getRouterStore = (state: ApplicationState) => state.router;

export const getLocation = createSelector(
  getRouterStore,
  (routerStore): LocationDescriptorObject => routerStore!.location,
);

export const getQS = createSelector(getLocation, (location) => location.search);

export const getParsedQS = createSelector(getQS, (querystring) =>
  qs.parse(querystring!.substring(1)),
);

export const getPathname = createSelector(getLocation, (location): string => location!.pathname!);

export const getQSResults = createSelector(
  getParsedQS,
  (queryParams): string => queryParams.results as string,
);

export const getQSHidden = createSelector(
  getParsedQS,
  (queryParams): boolean => !!queryParams.hidden,
);

export const getQSOrderStatus = createSelector(
  getParsedQS,
  (queryParams): string => queryParams.order_status as string,
);

export const getQSSort = createSelector(
  getParsedQS,
  (queryParams): string => (queryParams.sort as string) || '',
);

export const getQSSortBy = createSelector(
  getParsedQS,
  (queryParams): string => (queryParams.sort_by as string) || '',
);

export const getQSOrderBy = createSelector(
  getParsedQS,
  (queryParams): string => (queryParams.order_by as string) || '',
);

export const getQSPage = createSelector(getParsedQS, (queryParams): number =>
  Number(queryParams.page || '1'),
);

export const getQSIdx = createSelector(getParsedQS, (queryParams): number =>
  Number(queryParams.idx || '0'),
);

export const getQSWeightUnit = createSelector(
  getParsedQS,
  (queryParams): string => queryParams.weight_unit as string,
);

export const getQSWeightLt = createSelector(
  getParsedQS,
  (queryParams): string => queryParams.weight_lt as string,
);

export const getQSWeightGt = createSelector(
  getParsedQS,
  (queryParams): string => queryParams.weight_gt as string,
);

export const getQSShippingMethod = createSelector(
  getParsedQS,
  (queryParams): string => queryParams.shipping_method as string,
);

export function sortedQSitems(
  items?: string | string[] | Record<number, string>,
): string[] | undefined {
  if (!items) {
    return;
  }

  if (typeof items === 'string') {
    return [items];
  }

  if (Array.isArray(items)) {
    return items.sort();
  }

  return Object.values(items).sort();
}

export const getQSItem = createSelector(getParsedQS, (queryParams): string[] | undefined => {
  return sortedQSitems(queryParams.item as string[] | Record<number, string> | undefined);
});

export const getQSIsInternational = createSelector(
  getParsedQS,
  (queryParams): string => queryParams.is_international as string,
);

export const getQSShopApp = createSelector(
  getParsedQS,
  (queryParams): string => queryParams.shop_app as string,
);

export const getQSStartDate = createSelector(
  getParsedQS,
  (queryParams): string => queryParams.start_date as string,
);

export const getQSEndDate = createSelector(
  getParsedQS,
  (queryParams): string => queryParams.end_date as string,
);

export const getQSSaved = createSelector(
  getParsedQS,
  (queryParams): string => queryParams.saved as string,
);

export const getQSQ = createSelector(getParsedQS, (queryParams): string => queryParams.q as string);

export const getOrderFiltersValues = createSelector(
  getQSIsInternational,
  getQSShippingMethod,
  getQSShopApp,
  getQSStartDate,
  getQSEndDate,
  getQSWeightUnit,
  getQSWeightLt,
  getQSWeightGt,
  getQSOrderStatus,
  getQSQ,
  getQSSortBy,
  getQSOrderBy,
  getQSItem,
  (
    isInternational,
    shippingMethod,
    shopApp,
    startDate,
    endDate,
    weightUnit,
    weightLt,
    weightGt,
    orderStatus,
    q,
    SortBy,
    OrderBy,
    item,
  ) => ({
    order_status: orderStatus,
    is_international: isInternational,
    shipping_method: shippingMethod,
    shop_app: shopApp,
    start_date: startDate,
    end_date: endDate,
    weight_unit: weightUnit,
    weight_lt: weightLt,
    weight_gt: weightGt,
    order_by: OrderBy,
    sort_by: SortBy,
    q,
    item: item,
  }),
);

export const getNotEmptyOrdersFilters = createSelector(
  getOrderFiltersValues,
  (ordersFiltersValues) =>
    Object.keys(ordersFiltersValues).filter(
      (key) => ordersFiltersValues[key as keyof ReturnType<typeof getOrderFiltersValues>],
    ),
);
